import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Download, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice, formatDateTime, formatSlotDate, formatSlotTime, getMonthKey, getMonthName, exportToCSV } from '../../lib/utils';

const AdminReports = () => {
  const [loading, setLoading] = useState(true);
  const [downloadsReport, setDownloadsReport] = useState(null);
  const [balancesReport, setBalancesReport] = useState([]);
  const [guideRequestsReport, setGuideRequestsReport] = useState(null);
  const [users, setUsers] = useState([]);
  const [monthKey, setMonthKey] = useState(getMonthKey());
  const [filterUserId, setFilterUserId] = useState('all');
  
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    note: '',
  });

  useEffect(() => {
    loadReports();
  }, [monthKey]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [downloadsRes, balancesRes, requestsRes, usersRes] = await Promise.all([
        adminAPI.getDownloadsReport({ month_key: monthKey }),
        adminAPI.getBalancesReport({ month_key: monthKey }),
        adminAPI.getGuideRequestsReport(),
        adminAPI.listUsers({ role: 'user' }),
      ]);
      setDownloadsReport(downloadsRes.data);
      setBalancesReport(balancesRes.data);
      setGuideRequestsReport(requestsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await adminAPI.recordPayment(
        selectedUser.user_id,
        Math.round(paymentForm.amount * 100),
        paymentForm.note
      );
      toast.success('Payment recorded');
      setPaymentDialogOpen(false);
      setPaymentForm({ amount: 0, note: '' });
      loadReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openPaymentDialog = (userBalance) => {
    setSelectedUser(userBalance);
    setPaymentForm({ amount: userBalance.balance_due / 100, note: '' });
    setPaymentDialogOpen(true);
  };

  const exportBalances = () => {
    const data = balancesReport.map(b => ({
      Name: b.user_name,
      Company: b.company_name || '',
      Email: b.user_email,
      Type: b.user_type,
      'Total Debits (EUR)': (b.total_debits / 100).toFixed(2),
      'Total Credits (EUR)': (b.total_credits / 100).toFixed(2),
      'Balance Due (EUR)': (b.balance_due / 100).toFixed(2),
    }));
    exportToCSV(data, `balances-${monthKey}`);
  };

  const exportDownloads = () => {
    const filteredDownloads = filterUserId === 'all' 
      ? downloadsReport?.downloads 
      : downloadsReport?.downloads?.filter(d => d.user_id === filterUserId);
    
    const data = (filteredDownloads || []).map(d => ({
      'Downloaded At': formatDateTime(d.downloaded_at),
      'User': d.user_name,
      'Company': d.company_name || '',
      'Email': d.user_email,
      'Monument': d.monument_name,
      'Access Date': formatSlotDate(d.slot_start_at),
      'Access Time': formatSlotTime(d.slot_start_at),
      'Category': d.category_label || '',
      'Price (EUR)': (d.price_eur_cents / 100).toFixed(2),
    }));
    
    const userName = filterUserId !== 'all' 
      ? users.find(u => u.id === filterUserId)?.name?.replace(/\s+/g, '_') || 'user'
      : 'all';
    exportToCSV(data, `downloads-${monthKey}-${userName}`);
  };

  // Generate month options
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = getMonthKey(d);
    monthOptions.push({ key, label: getMonthName(key) });
  }

  return (
    <AdminLayout title="Reports" subtitle="Downloads, balances, and guide requests">
      <div className="space-y-4">
        {/* Month Filter */}
        <div className="flex items-center gap-4">
          <Label>Period:</Label>
          <Select value={monthKey} onValueChange={setMonthKey}>
            <SelectTrigger className="w-48" data-testid="month-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(opt => (
                <SelectItem key={opt.key} value={opt.key}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="downloads" className="space-y-4">
          <TabsList>
            <TabsTrigger value="downloads" data-testid="tab-downloads">Downloads</TabsTrigger>
            <TabsTrigger value="balances" data-testid="tab-balances">Balances</TabsTrigger>
            <TabsTrigger value="guide-requests" data-testid="tab-guide-requests">Guide Requests</TabsTrigger>
          </TabsList>

          <TabsContent value="downloads">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Downloads Report - {getMonthName(monthKey)}</CardTitle>
                  <div className="flex items-center gap-4 mt-3">
                    <Select value={filterUserId} onValueChange={setFilterUserId}>
                      <SelectTrigger className="w-56" data-testid="filter-user">
                        <SelectValue placeholder="Filter by user" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} {u.company_name ? `(${u.company_name})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={exportDownloads} data-testid="export-downloads-btn">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </div>
                {downloadsReport && (
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Total Downloads: {
                      filterUserId === 'all' 
                        ? downloadsReport.total_count 
                        : downloadsReport.downloads?.filter(d => d.user_id === filterUserId).length || 0
                    }</p>
                    <p className="text-lg font-semibold">{formatPrice(
                      filterUserId === 'all'
                        ? downloadsReport.total_revenue_cents
                        : downloadsReport.downloads?.filter(d => d.user_id === filterUserId).reduce((sum, d) => sum + d.price_eur_cents, 0) || 0
                    )}</p>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8"><div className="spinner" /></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Downloaded At</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Monument</TableHead>
                        <TableHead>Access Date</TableHead>
                        <TableHead>Access Time</TableHead>
                        <TableHead>Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {downloadsReport?.downloads
                        ?.filter(dl => filterUserId === 'all' || dl.user_id === filterUserId)
                        .slice(0, 100)
                        .map((dl) => (
                          <TableRow key={dl.id}>
                            <TableCell className="text-sm">{formatDateTime(dl.downloaded_at)}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{dl.user_name}</p>
                                <p className="text-xs text-slate-500">{dl.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-500">{dl.company_name || '-'}</TableCell>
                            <TableCell className="font-medium">{dl.monument_name}</TableCell>
                            <TableCell>{formatSlotDate(dl.slot_start_at)}</TableCell>
                            <TableCell>{formatSlotTime(dl.slot_start_at)}</TableCell>
                            <TableCell className="font-semibold">{formatPrice(dl.price_eur_cents)}</TableCell>
                          </TableRow>
                        ))}
                      {(!downloadsReport?.downloads || downloadsReport.downloads.filter(dl => filterUserId === 'all' || dl.user_id === filterUserId).length === 0) && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                            No downloads for this period
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="balances">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>User Balances - {getMonthName(monthKey)}</CardTitle>
                <Button variant="outline" onClick={exportBalances} data-testid="export-balances-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8"><div className="spinner" /></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Debits</TableHead>
                        <TableHead className="text-right">Credits</TableHead>
                        <TableHead className="text-right">Balance Due</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {balancesReport.map((balance) => (
                        <TableRow key={balance.user_id} data-testid={`balance-row-${balance.user_id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{balance.user_name}</p>
                              <p className="text-sm text-slate-500">{balance.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-500">{balance.company_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{balance.user_type}</Badge>
                          </TableCell>
                          <TableCell className="text-right">{formatPrice(balance.total_debits)}</TableCell>
                          <TableCell className="text-right">{formatPrice(balance.total_credits)}</TableCell>
                          <TableCell className="text-right font-semibold">
                            <span className={balance.balance_due > 0 ? 'text-red-600' : 'text-emerald-600'}>
                              {formatPrice(balance.balance_due)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {balance.balance_due > 0 && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openPaymentDialog(balance)}
                                data-testid={`record-payment-btn-${balance.user_id}`}
                              >
                                <DollarSign className="w-4 h-4 mr-1" />
                                Record Payment
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {balancesReport.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                            No balance data for this period
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="guide-requests">
            <Card>
              <CardHeader>
                <CardTitle>Guide Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8"><div className="spinner" /></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Guide</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {guideRequestsReport?.requests?.slice(0, 50).map((req) => (
                        <TableRow key={req.id}>
                          <TableCell>{formatDateTime(req.requested_start_at)}</TableCell>
                          <TableCell>{req.city_id}</TableCell>
                          <TableCell>{req.category_id}</TableCell>
                          <TableCell>
                            <Badge className={
                              req.status === 'pending' ? 'badge-warning' :
                              req.status === 'accepted' ? 'badge-success' :
                              req.status === 'completed' ? 'badge-info' : 'badge-neutral'
                            }>
                              {req.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{req.guide_user_id || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {(!guideRequestsReport?.requests || guideRequestsReport.requests.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                            No guide requests found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4 mt-4">
            {selectedUser && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedUser.user_name}</p>
                <p className="text-sm text-slate-500">Balance due: {formatPrice(selectedUser.balance_due)}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Payment Amount (EUR)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) })}
                required
                data-testid="payment-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={paymentForm.note}
                onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })}
                placeholder="Payment reference or note"
                required
                data-testid="payment-note-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-payment-btn">
                Record Payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminReports;
